import type {FieldError, FieldErrors, Resolver} from 'react-hook-form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {generateFormSchema} from 'src/types/FormV2Schema';
import {
  buildFieldsByInvariantId,
  evaluateVisibilityMap,
} from './visibilityEvaluator';

/**
 * Builds a react-hook-form resolver that validates against the zod schema
 * generated from the form metadata.
 *
 * Visibility-aware: hidden fields are excluded from the schema, so they can
 * never produce errors (same contract as the legacy useFormValidation hook).
 * The schema is cached per set of visible fields since visibility changes far
 * less often than values.
 */
export function createProactResolver(
  formMeta: IFormFieldV2[]
): Resolver<IFormDataV2> {
  let cachedKey = '';
  let cachedSchema: ReturnType<typeof generateFormSchema> | null = null;
  const fieldsByInvariantId = buildFieldsByInvariantId(formMeta);

  return async values => {
    const visibility = evaluateVisibilityMap(
      formMeta,
      values,
      fieldsByInvariantId
    );
    const visibleFields = formMeta.filter(field => visibility[field.name]);

    const key = visibleFields.map(field => field.name).join('|');
    if (!cachedSchema || key !== cachedKey) {
      cachedSchema = generateFormSchema(visibleFields);
      cachedKey = key;
    }

    const result = cachedSchema.safeParse(values);
    if (result.success) {
      return {values, errors: {}};
    }

    const errors: FieldErrors<IFormDataV2> = {};
    for (const issue of result.error.errors) {
      const name = issue.path[0]?.toString();
      if (!name || errors[name] || !visibility[name]) continue;
      errors[name] = {
        type: issue.code,
        message: issue.message,
      } as FieldError;
    }

    if (Object.keys(errors).length === 0) {
      return {values, errors: {}};
    }
    return {values: {}, errors};
  };
}
